import {
  ShieldCheckIcon,
  ClockIcon,
  Bars3BottomLeftIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";

export default function FoodSafety() {
  const safetyGuidelines = [
    {
      title: "Food Freshness",
      icon: <CheckCircleIcon className="w-6 h-6" />,
      description: "Only donate food that is fresh and safe for consumption",
      details: [
        "Check expiry dates before donating",
        "Ensure food looks, smells, and tastes normal",
        "Don't donate food that's been left out for more than 2 hours",
        "Verify packaging is intact and undamaged",
      ],
    },
    {
      title: "Temperature Control",
      icon: <Bars3BottomLeftIcon className="w-6 h-6" />,
      description: "Maintain proper temperatures to prevent bacterial growth",
      details: [
        "Keep cold foods below 4°C (40°F)",
        "Keep hot foods above 60°C (140°F)",
        // "Use insulated containers for transport",
        "Don't leave perishables in the temperature danger zone (4-60°C)",
      ],
    },
    {
      title: "Hygiene Standards",
      icon: <ShieldCheckIcon className="w-6 h-6" />,
      description:
        "Follow proper hygiene practices during preparation and handling",
      details: [
        "Wash hands thoroughly before handling food",
        "Use clean utensils and containers",
        "Sanitize preparation surfaces",
        "Wear clean clothing and hair coverings if applicable",
      ],
    },
    {
      title: "Proper Labeling",
      icon: <DocumentTextIcon className="w-6 h-6" />,
      description: "Provide clear information about the food being donated",
      details: [
        "Include preparation date and time",
        "List all ingredients clearly",
        "Mark any allergens prominently",
        "Provide reheating instructions if needed",
      ],
    },
  ];

  const allergens = [
    "Gluten (wheat, barley)",
    "Dairy products (milk, cheese, yogurt)",
    "Eggs and egg products",
    "Tree nuts (almonds, walnuts, cashews)",
    "Peanuts",
    "Fish and seafood",
    "Shellfish (shrimp, crab, lobster)",
    "Soy products",
    "Sesame seeds",
  ];

  const prohibitedItems = [
    "Homemade food from unlicensed kitchens",
    "Food past its expiry date",
    "Previously served or partially consumed food",
    "Food stored at unsafe temperatures",
    "Alcoholic beverages",
    "Food with unknown ingredients or preparation methods",
    "Damaged or opened packaging",
    "Food prepared in unsanitary conditions",
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/20">
      {/* Header */}
      <section className="relative py-16 overflow-hidden text-white lg:py-20">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-600 via-teal-700 to-cyan-600"></div>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.1%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-50"></div>

        <div className="relative z-10 text-center container-modern">
          <div className="inline-flex items-center px-6 py-3 mb-8 border-2 rounded-full shadow-2xl bg-white/20 backdrop-blur-xl border-white/30">
            <span className="text-sm font-bold">🛡️ Safety First</span>
          </div>

          <ShieldCheckIcon className="w-16 h-16 mx-auto mb-6 text-white" />
          <h1 className="mb-6 text-3xl font-bold md:text-4xl lg:text-5xl">
            Food Safety Guidelines
          </h1>
          <p className="max-w-3xl mx-auto text-lg text-white/95 font-medium md:text-xl">
            Ensuring safe, nutritious food for everyone in our community through
            proper handling, storage, and distribution practices.
          </p>
        </div>
      </section>

      <div className="px-4 py-12 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* Safety Guidelines */}
        <section className="mb-16">
          <div className="text-center mb-12">
            <h2 className="mb-6 text-2xl font-bold text-slate-900 lg:text-3xl">
              Essential Safety Guidelines
            </h2>
            <p className="max-w-2xl mx-auto text-base text-slate-600 font-medium">
              Follow these guidelines to ensure food safety for everyone
            </p>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {safetyGuidelines.map((guideline, index) => (
              <div key={index} className="card-static">
                <div className="flex items-center mb-4 space-x-3">
                  <div className="p-2 rounded-lg bg-emerald-100 text-emerald-600">
                    {guideline.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900">
                    {guideline.title}
                  </h3>
                </div>
                <p className="mb-4 text-sm text-slate-600">
                  {guideline.description}
                </p>
                <ul className="space-y-2">
                  {guideline.details.map((detail, idx) => (
                    <li key={idx} className="flex items-start space-x-2">
                      <CheckCircleIcon className="w-4 h-4 mt-0.5 text-emerald-500 flex-shrink-0" />
                      <span className="text-sm text-slate-700">{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Allergen Information */}
        <section className="mb-16">
          <div className="card-static">
            <div className="flex items-center mb-4 space-x-3">
              <ExclamationTriangleIcon className="w-6 h-6 text-orange-500" />
              <h2 className="text-xl font-bold text-slate-900">
                Common Allergens to Declare
              </h2>
            </div>
            <p className="mb-6 text-sm text-slate-600">
              Always clearly label any of these common allergens present in your
              food donations:
            </p>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
              {allergens.map((allergen, index) => (
                <div
                  key={index}
                  className="flex items-center p-3 border border-orange-200 rounded-lg bg-orange-50"
                >
                  <ExclamationTriangleIcon className="w-4 h-4 mr-2 text-orange-500" />
                  <span className="text-xs font-medium text-orange-800">
                    {allergen}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Prohibited Items */}
        <section className="mb-16">
          <div className="card-static">
            <div className="flex items-center mb-4 space-x-3">
              <ExclamationTriangleIcon className="w-6 h-6 text-red-500" />
              <h2 className="text-xl font-bold text-slate-900">
                Items We Cannot Accept
              </h2>
            </div>
            <p className="mb-6 text-sm text-slate-600">
              For the safety of our community, we cannot accept the following
              items:
            </p>
            <div className="space-y-3">
              {prohibitedItems.map((item, index) => (
                <div
                  key={index}
                  className="flex items-start p-3 border border-red-200 rounded-lg bg-red-50"
                >
                  <ExclamationTriangleIcon className="w-4 h-4 mt-0.5 mr-3 text-red-500 flex-shrink-0" />
                  <span className="text-sm text-red-800">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Temperature Guidelines */}
        <section className="mb-16">
          <div className="text-center mb-8">
            <h2 className="mb-4 text-xl font-bold text-slate-900">
              Temperature Guidelines
            </h2>
            <p className="text-sm text-slate-600">
              Maintain proper temperatures to ensure food safety
            </p>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="card-static">
              <div className="flex items-center mb-4 space-x-3">
                <Bars3BottomLeftIcon className="w-5 h-5 text-blue-500" />
                <h3 className="text-lg font-semibold text-slate-900">
                  Cold Foods
                </h3>
              </div>
              <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
                <p className="mb-2 text-xl font-bold text-blue-800">
                  Below 4°C (40°F)
                </p>
                <p className="text-xs text-blue-700">
                  Dairy products, fresh meat, seafood, prepared salads, and cut
                  fruits
                </p>
              </div>
            </div>
            <div className="card-static">
              <div className="flex items-center mb-4 space-x-3">
                <Bars3BottomLeftIcon className="w-5 h-5 text-red-500" />
                <h3 className="text-lg font-semibold text-slate-900">
                  Hot Foods
                </h3>
              </div>
              <div className="p-4 border border-red-200 rounded-lg bg-red-50">
                <p className="mb-2 text-xl font-bold text-red-800">
                  Above 60°C (140°F)
                </p>
                <p className="text-xs text-red-700">
                  Cooked meals, soups, stews, and other prepared hot dishes
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Time Guidelines */}
        <section className="mb-16">
          <div className="card-static">
            <div className="flex items-center mb-4 space-x-3">
              <ClockIcon className="w-6 h-6 text-purple-500" />
              <h2 className="text-xl font-bold text-slate-900">
                Time Guidelines for Food Safety
              </h2>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="p-4 border border-purple-200 rounded-lg bg-purple-50">
                <h4 className="mb-2 text-sm font-semibold text-purple-800">
                  2 Hour Rule
                </h4>
                <p className="text-xs text-purple-700">
                  Don't leave perishable food at room temperature for more than
                  2 hours
                </p>
              </div>
              <div className="p-4 border border-purple-200 rounded-lg bg-purple-50">
                <h4 className="mb-2 text-sm font-semibold text-purple-800">
                  1 Hour Rule
                </h4>
                <p className="text-xs text-purple-700">
                  In hot weather (above 32°C), reduce this to 1 hour maximum
                </p>
              </div>
              <div className="p-4 border border-purple-200 rounded-lg bg-purple-50">
                <h4 className="mb-2 text-sm font-semibold text-purple-800">
                  Same Day
                </h4>
                <p className="text-xs text-purple-700">
                  Prepared foods should ideally be consumed the same day
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Emergency Contact */}
        <section>
          <div className="p-6 text-center border border-red-200 rounded-lg bg-red-50">
            <ExclamationTriangleIcon className="w-10 h-10 mx-auto mb-4 text-red-500" />
            <h3 className="mb-4 text-xl font-bold text-slate-900">
              Report Food Safety Concerns
            </h3>
            <p className="mb-6 text-sm text-slate-700">
              If you receive food that appears unsafe or have concerns about
              food safety, please report it immediately.
            </p>
            <div className="flex flex-col justify-center gap-4 sm:flex-row">
              <Link
                to="/contact"
                className="px-6 py-3 text-sm font-medium text-white bg-red-600 rounded-lg text-center"
              >
                Report Safety Issue
              </Link>
              <Link
                to="/help"
                className="px-6 py-3 text-sm font-medium text-red-600 border border-red-600 rounded-lg text-center"
              >
                Contact Support
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
